import { useEffect, useState, useCallback } from 'react';
import {
  Box, Flex, Text, Button, Divider, Spinner, Heading, Wrap, WrapItem,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { getAuditLogs, type AuditLogPageDto } from '../api/organizationApi';

const ENTITY_TYPES = ['Organization', 'Location', 'User', 'Invitation'] as const;

export function AuditLogPage() {
  const { t } = useTranslation();
  const [data, setData] = useState<AuditLogPageDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [entityType, setEntityType] = useState<string>('');
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getAuditLogs({
        entityType: entityType || undefined,
        page,
        pageSize,
      });
      setError(null);
      setData(result);
    } catch (e: unknown) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [entityType, page]);

  useEffect(() => { load(); }, [load]);

  const totalPages = data ? Math.ceil(data.totalCount / pageSize) : 0;

  return (
    <Box flex={1} p={4} maxW="1000px">
      <Heading as="h2" size="lg" mb={4}>{t('auditLog.title')}</Heading>

      {error && (
        <Flex bg="red.50" p={3} borderRadius="md" mb={4}>
          <Text color="red.500">{error}</Text>
        </Flex>
      )}

      {/* Filters */}
      <Wrap spacing={3} align="center" mb={4}>
        <WrapItem>
          <Text fontWeight="600">{t('auditLog.filterByEntity')}:</Text>
        </WrapItem>
        <WrapItem>
          <Button
            size="sm"
            colorScheme={entityType === '' ? 'brand' : 'gray'}
            variant={entityType === '' ? 'solid' : 'outline'}
            onClick={() => { setEntityType(''); setPage(1); }}
          >
            {t('auditLog.allEntities')}
          </Button>
        </WrapItem>
        {ENTITY_TYPES.map((et) => (
          <WrapItem key={et}>
            <Button
              size="sm"
              colorScheme={entityType === et ? 'brand' : 'gray'}
              variant={entityType === et ? 'solid' : 'outline'}
              onClick={() => { setEntityType(et); setPage(1); }}
            >
              {et}
            </Button>
          </WrapItem>
        ))}
      </Wrap>

      <Divider mb={4} />

      {loading ? (
        <Flex justify="center" p={6}>
          <Spinner size="lg" />
        </Flex>
      ) : !data || data.items.length === 0 ? (
        <Flex justify="center" p={6}>
          <Text color="gray.500">{t('auditLog.noEntries')}</Text>
        </Flex>
      ) : (
        <Box>
          {data.items.map((entry) => (
            <Flex
              key={entry.id}
              p={3}
              borderRadius="md"
              bg="gray.50"
              gap={4}
              align="center"
              flexWrap="wrap"
              mb={2}
            >
              <Text fontSize="sm" color="gray.500" minW="140px">
                {new Date(entry.createdAt).toLocaleString()}
              </Text>
              <Text fontSize="md" fontWeight="600" minW="100px">
                {entry.action}
              </Text>
              <Text fontSize="md" color="gray.600" minW="100px">
                {entry.entityType}
              </Text>
              {entry.fieldName && (
                <Text fontSize="sm" color="gray.500">
                  {entry.fieldName}: {entry.oldValue ?? '—'} → {entry.newValue ?? '—'}
                </Text>
              )}
              {entry.userName && (
                <Text fontSize="sm" color="gray.500" ml="auto">
                  {entry.userName}
                </Text>
              )}
            </Flex>
          ))}
        </Box>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Flex gap={2} justify="center" pt={3}>
          <Button size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} isDisabled={page <= 1}>
            {t('common.back')}
          </Button>
          <Text alignSelf="center" fontSize="md" color="gray.600">
            {page} / {totalPages}
          </Text>
          <Button size="sm" onClick={() => setPage((p) => p + 1)} isDisabled={page >= totalPages}>
            {t('common.next')}
          </Button>
        </Flex>
      )}
    </Box>
  );
}
